import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Badge,
  IconButton,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import PropTypes from "prop-types";
import CommandPalette from "./CommandPalette";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useAuth } from "../../auth/AuthProvider";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { getUnreadCount } from "../../services/notifications.api";
import useNotificationsSocket from "../../hooks/useNotificationsSocket";
import { tokens } from "../../theme";

// The badge polls because most notification types are created with no accompanying
// socket emit — the socket hook below only shortens the delay for the types that do
// signal, it cannot replace the poll.
const UNREAD_POLL_MS = 30000;

export const Header = ({ isMobile = false, mobileNavOpen = false, onToggleSidebar }) => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  // Durable, all-time unread count straight from the server — not a tally of what
  // arrived since this tab connected, so it survives a reload and matches the page.
  const refreshUnreadCount = useCallback(async () => {
    try {
      setUnreadCount(await getUnreadCount());
    } catch {
      // Keep the last known count: a stale badge is better than one that resets to
      // zero on a single failed request.
    }
  }, []);

  useEffect(() => {
    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, UNREAD_POLL_MS);
    return () => clearInterval(interval);
  }, [refreshUnreadCount]);

  // The Notifications page owns its own unread state and has no other channel
  // back to this header, so it announces mark-read/mark-all-read via this event
  // instead of the badge waiting out the 30s poll to catch up.
  useEffect(() => {
    window.addEventListener("botriq:notifications-unread-changed", refreshUnreadCount);
    return () =>
      window.removeEventListener("botriq:notifications-unread-changed", refreshUnreadCount);
  }, [refreshUnreadCount]);

  // Live top-up on the signals the backend does emit, so an approval or an access
  // change lands on the badge immediately instead of up to 30s later.
  useNotificationsSocket(refreshUnreadCount);

  // Global ⌘K / Ctrl+K opens the command palette.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate("/login", { replace: true });
  };

  // Open the Settings page on a specific tab. The tab is passed via navigation
  // state (and mirrored to sessionStorage) so Settings switches to it even when
  // already open. Shared by the Account and Payment menu items.
  const openSettingsTab = (tab) => {
    setAnchorEl(null);
    try {
      sessionStorage.setItem("botriq:settingsTab", tab);
    } catch {
      /* ignore */
    }
    navigate("/settings", { state: { tab } });
  };

  // The company logo is a presigned S3 URL that expires, and a session restored
  // from localStorage can be days old. Re-sign it on mount so a stale URL never
  // renders as a broken image; the value from login covers the first paint.
  useEffect(() => {
    let alive = true;
    const refreshCompanyLogo = async () => {
      try {
        const token = localStorage.getItem("portalToken");
        if (!token) return;
        const res = await apiFetch(`${CONFIG.BASE_URL}/api/v1/auth/company-logo`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        // Only write when it actually changed — updateUser persists to
        // localStorage, so an unconditional call would rewrite it on every mount.
        if (alive && typeof data.url === "string" && data.url !== user?.companyLogo) {
          updateUser({ companyLogo: data.url });
        }
      } catch {
        // A logo is decoration: on failure the header keeps the login-time URL
        // and falls back to initials if that has expired.
      }
    };
    refreshCompanyLogo();
    return () => {
      alive = false;
    };
    // Mount-only: re-running on companyLogo would loop against its own update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // An S3 presigned URL is absolute; the legacy '/uploads/logos/...' path is
  // relative and still needs the API origin prefixed.
  const rawLogo = user?.companyLogo || null;
  const resolveLogo = (logo) => (logo.startsWith("http") ? logo : `${CONFIG.BASE_URL}${logo}`);
  const logoSrc = rawLogo ? resolveLogo(rawLogo) : null;

  return (
    <>
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
      <Box
        component="header"
        sx={{
          height: 64,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: { xs: 2, sm: 3 },
          mx: 1.5,
          mt: 1.5,
          mb: 1,
          borderRadius: "18px",
          border: `1px solid ${tokens.border}`,
          backgroundColor: tokens.surface,
          boxShadow:
            "0 10px 30px -5px rgba(15, 23, 42, 0.08), 0 4px 12px -2px rgba(61, 106, 224, 0.04)",
          position: "sticky",
          top: 0,
          zIndex: 30,
        }}
      >
        {/* Hamburger: the ONLY way to open the drawer on mobile, because the
            sidebar's own toggle is off-canvas while it's closed. Hidden on
            desktop, where the docked rail carries its own collapse control. */}
        {isMobile && (
          <IconButton
            size="small"
            onClick={onToggleSidebar}
            aria-label="Open navigation"
            aria-expanded={mobileNavOpen}
            sx={{
              color: tokens.text2,
              background: tokens.surface2,
              border: `1px solid ${tokens.border}`,
              borderRadius: "8px",
              width: 34,
              height: 34,
              flex: "none",
              ml: -1,
            }}
          >
            <MenuIcon sx={{ fontSize: 19 }} />
          </IconButton>
        )}

        {/* Pinned to the sidebar's own 230px (see Sidebar.css) so the divider
            lands on the rail's right edge. Mobile has no rail to align to. */}
        <Stack
          direction="row"
          alignItems="center"
          gap={1.5}
          sx={{
            flex: "none",
            minWidth: 0,
            width: isMobile ? "auto" : 230,
            ml: isMobile ? 0 : -3,
            pl: isMobile ? 0 : 3,
            borderRight: isMobile ? "none" : `1px solid ${tokens.border}`,
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            aria-label="Botriq — go to Dashboard"
            title="Go to Dashboard"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              border: "none",
              background: "none",
              cursor: "pointer",
              padding: 0,
              fontFamily: "inherit",
              textAlign: "left",
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: `linear-gradient(135deg, ${tokens.accent}, #22c1dc)`,
                display: "grid",
                placeItems: "center",
                flex: "none",
                overflow: "hidden",
                boxShadow: "0 4px 10px -2px rgba(61, 106, 224, 0.35)",
              }}
            >
              <Box
                component="img"
                src="/assets/Botriq-icon.svg"
                alt=""
                aria-hidden="true"
                sx={{
                  width: 28,
                  height: 28,
                  objectFit: "contain",
                  filter: "brightness(0) invert(1)",
                }}
              />
            </Box>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 800,
                  letterSpacing: "-0.01em",
                  color: tokens.text,
                  lineHeight: 1.15,
                }}
              >
                Botriq
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  color: tokens.accent,
                  mt: "2px",
                }}
              >
                Customer Portal
              </Typography>
            </Box>
          </button>
        </Stack>

        <Box
          component="button"
          type="button"
          onClick={() => setCmdOpen(true)}
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 1,
            ml: 1.75,
            flex: 1,
            maxWidth: 520,
            px: 1.4,
            py: "5px",
            height: 32,
            borderRadius: "9px",
            border: `1px solid ${tokens.border}`,
            background: tokens.surface2,
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "left",
            "&:hover": { borderColor: tokens.borderStrong },
          }}
        >
          <SearchIcon sx={{ fontSize: 16, color: tokens.text3 }} />
          <Typography sx={{ fontSize: 13, color: tokens.text3, flex: 1 }}>
            Search robots, ads…
          </Typography>
          <Box
            component="kbd"
            sx={{
              fontSize: 10.5,
              fontFamily: "'JetBrains Mono',monospace",
              color: tokens.text3,
              border: `1px solid ${tokens.border}`,
              borderRadius: "5px",
              px: "5px",
              py: "1px",
            }}
          >
            ctrl + K
          </Box>
        </Box>

        <Stack direction="row" alignItems="center" gap={1} sx={{ ml: "auto" }}>
          {/* Notifications */}
          <IconButton
            size="small"
            onClick={() => navigate("/notifications")}
            sx={{
              color: tokens.text2,
              background: tokens.surface2,
              border: `1px solid ${tokens.border}`,
              borderRadius: "8px",
              width: 32,
              height: 32,
              "&:hover": { borderColor: tokens.borderStrong },
            }}
          >
            {/* A dot, not a count: the number is only as fresh as the last poll
                (see the mark-all-read race in useNotifications.js). */}
            <Badge
              variant="dot"
              invisible={unreadCount <= 0}
              sx={{
                "& .MuiBadge-badge": {
                  background: tokens.danger,
                  minWidth: 9,
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                },
              }}
            >
              {unreadCount > 0 ? (
                <NotificationsActiveIcon sx={{ fontSize: 18, color: tokens.accent }} />
              ) : (
                <NotificationsNoneIcon sx={{ fontSize: 18 }} />
              )}
            </Badge>
          </IconButton>

          <Box sx={{ width: "1px", height: 24, background: tokens.border, flex: "none" }} />

          {/* User Avatar / Menu */}
          <Stack
            component="button"
            type="button"
            direction="row"
            alignItems="center"
            gap={1}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              border: "none",
              background: "transparent",
              cursor: "pointer",
              pl: 0.25,
              py: 0.25,
              pr: 0.5,
              borderRadius: "10px",
              "&:hover": { background: tokens.surface2 },
            }}
          >
            {logoSrc ? (
              <Avatar src={logoSrc} sx={{ width: 32, height: 32, borderRadius: "9px" }} />
            ) : (
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "9px",
                  background: `linear-gradient(135deg, ${tokens.accent}, #22c1dc)`,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {(user?.name || user?.companyName || "B").slice(0, 2).toUpperCase()}
              </Avatar>
            )}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                textAlign: "left",
              }}
            >
              <Typography
                sx={{ fontSize: 13, fontWeight: 700, color: tokens.text, lineHeight: 1.15 }}
              >
                {user?.name || "Account"}
              </Typography>
              {/* Secondary line carries the tenant now that the primary line is
                  the signed-in user, so the company stays visible in the bar. */}
              <Typography sx={{ fontSize: 10.5, color: tokens.text3, lineHeight: 1.2 }}>
                {user?.companyName || "Customer Portal"}
              </Typography>
            </Box>
            <Box sx={{ display: { xs: "none", sm: "flex" } }}>
              <ExpandMoreIcon sx={{ fontSize: 18, color: tokens.text3 }} />
            </Box>
          </Stack>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  borderRadius: "12px",
                  minWidth: 220,
                  boxShadow: tokens.shadowLg,
                  border: `1px solid ${tokens.borderStrong}`,
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
              {logoSrc ? (
                <Avatar src={logoSrc} sx={{ width: 34, height: 34, borderRadius: "8px" }} />
              ) : (
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "8px",
                    background: tokens.accent2,
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {(user?.name || user?.companyName || "B").slice(0, 2).toUpperCase()}
                </Avatar>
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: tokens.text }}>
                  {user?.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11.5,
                    color: tokens.text3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user?.email}
                </Typography>
              </Box>
            </Box>
            <Divider />
            {/* Account & Payment open the Settings page on the matching tab
              (see openSettingsTab). */}
            <MenuItem
              onClick={() => openSettingsTab("account")}
              sx={{ fontSize: 13, gap: 1.5, py: 1 }}
            >
              <PersonOutlineIcon sx={{ fontSize: 16, color: tokens.text2 }} /> Account
            </MenuItem>
            <MenuItem
              onClick={() => openSettingsTab("payment")}
              sx={{ fontSize: 13, gap: 1.5, py: 1 }}
            >
              <CreditCardIcon sx={{ fontSize: 16, color: tokens.text2 }} /> Payment
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate("/settings");
              }}
              sx={{ fontSize: 13, gap: 1.5, py: 1 }}
            >
              <SettingsOutlinedIcon sx={{ fontSize: 16, color: tokens.text2 }} /> Settings
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={handleLogout}
              sx={{ fontSize: 13, gap: 1.5, py: 1, color: tokens.danger }}
            >
              <LogoutIcon sx={{ fontSize: 16, color: tokens.danger }} /> Logout
            </MenuItem>
          </Menu>
        </Stack>
      </Box>
    </>
  );
};

Header.propTypes = {
  isMobile: PropTypes.bool,
  mobileNavOpen: PropTypes.bool,
  onToggleSidebar: PropTypes.func,
};
